"use client"

import { useEffect, useState } from "react"
import { AnimatedResult } from "@/components/crystality/animated-result"
import { Button } from "@/components/ui/button"
import { ArrowRight, RotateCcw, Download } from "lucide-react"
import Link from "next/link"

interface SuccessStepProps {
  type: "success" | "error"
}

export function SuccessStep({ type }: SuccessStepProps) {
  const [showActions, setShowActions] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setShowActions(true), 1200)
    return () => clearTimeout(timer)
  }, [])

  if (type === "error") {
    return (
      <div className="space-y-8 text-center">
        <AnimatedResult
          type="error"
          title="Payment Failed"
          description="Something went wrong with your payment. Please try again."
        />
        
        {showActions && (
          <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <Button
              variant="outline"
              className="w-full bg-transparent"
              onClick={() => window.location.reload()}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
            
            <p className="text-sm text-muted-foreground">
              Need help?{" "}
              <a
                href="mailto:support@crystality.win"
                className="text-primary hover:underline"
              >
                Contact Support
              </a>
            </p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-8 text-center">
      <AnimatedResult
        type="success"
        title="Welcome to Crystality!"
        description="Your account has been created successfully."
      />
      
      {showActions && (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <Button
            asChild
            className="w-full h-12 gradient-purple hover:opacity-90 transition-opacity text-white font-medium"
          >
            <Link href="/dashboard">
              <Download className="mr-2 h-4 w-4" />
              Go to Dashboard
            </Link>
          </Button>
          
          <div className="flex items-center justify-center gap-4">
            <Link
              href="/discord"
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Join Discord
            </Link>
            <span className="text-muted-foreground">|</span>
            <Link
              href="https://crystality.win"
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Visit Website
            </Link>
          </div>
          
          <p className="text-xs text-muted-foreground">
            Check your email for your license key and receipt
          </p>
        </div>
      )}
    </div>
  )
}
